import { Controller } from "react-hook-form"
import { useState, useRef } from "react"
import useOnClickOutside from "@/lib/hooks/useOnClickOutside"
import Checkmark from "@/common/icons/checkmark"
import { Caret } from "@/common/icons/caret"
import Plus from "@/components/common/icons/plus"
import Minus from "@/components/common/icons/minus"
import Close from "@/common/icons/close"
import classNames from "classnames"
import styles from "./filter-control.module.css"

export const FilterControl = ({
  className,
  control,
  hierarchical = false,
  isOpen,
  label,
  name,
  onReset,
  onSelect,
  options,
  selected = [],
  setOpen,
  strings = { all: "Alla", singular: "vald", plural: "valda" }
}) => {
  const elemRef = useRef(null)
  const [openParents, setOpenParents] = useState([])
  useOnClickOutside(elemRef, () => setOpen(name), isOpen)

  const checkCount = selected.length
  const currentChoice =
    checkCount === 0
      ? strings?.all
      : checkCount +
        (checkCount === 1 ? " " + strings?.singular : " " + strings?.plural)

  const selectedParents = getSelectedParents(selected, options)

  return (
    <Controller
      {...{ control, name }}
      render={({ field }) => {
        const hasValues = field.value && field.value.length > 0
        return (
          <div
            className={classNames(styles.controller, className, {
              "has-value": hasValues,
              "is-open": isOpen,
              "is-hierarchical": hierarchical
            })}
            ref={elemRef}>
            <div
              className={classNames({
                checkboxes: true,
                "has-shadow": isOpen
              })}>
              <header onClick={() => setOpen(name)}>
                <div className="current-choice">
                  <span>{label}</span>
                  {currentChoice}
                </div>
                {hasValues && (
                  <div
                    className="clear-filter"
                    onClick={(e) => {
                      e.stopPropagation()
                      onReset(name)
                    }}>
                    <Close />
                  </div>
                )}
                <Caret />
              </header>
              {isOpen && (
                <div className="open-filter">
                  <div className="overlay"></div>
                  <ul>
                    {options.length > 0 &&
                      options.map((option) => {
                        const hasChildren =
                          options.filter((item) => item.parent === option.value)
                            .length > 0

                        if (
                          hierarchical &&
                          option?.parent > 0 &&
                          !openParents.includes(option.parent)
                        ) {
                          return null
                        }

                        return (
                          <Checkbox
                            key={option.value}
                            isParent={hierarchical && option?.parent === 0}
                            isChild={hierarchical && option?.parent > 0}
                            isOpen={
                              hierarchical && openParents.includes(option.value)
                            }
                            hasChildren={hasChildren}
                            hasSelectedChildren={
                              hierarchical &&
                              selectedParents.includes(option.value)
                            }
                            setOpen={setOpenParents}
                            name={name}
                            isChecked={selected.includes(option.value)}
                            {...{ option, onSelect }}
                          />
                        )
                      })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )
      }}
    />
  )
}

const Checkbox = ({
  option,
  name,
  isParent,
  isChild,
  isOpen,
  setOpen,
  hasChildren,
  hasSelectedChildren,
  isChecked,
  onSelect
}) => {
  return (
    <li
      onClick={() => {
        onSelect(name, option.value)
        isParent && !isChecked && setOpen([option.value])
      }}
      className={classNames("checkbox", {
        "is-checked": isChecked,
        "is-child": isChild,
        "is-parent": isParent,
        "has-checked-children": hasSelectedChildren
      })}>
      <div className="square">
        {isChecked && <Checkmark className="w-3" fillColor="#101010" />}
        {hasSelectedChildren && !isChecked && <div className="parent-dot" />}
      </div>
      <div className="label-wrapper">
        <span className="label">{option.label}</span>
        {hasChildren && isParent && !isOpen && (
          <div
            className="icon-wrapper"
            onClick={(e) => {
              e.stopPropagation()
              setOpen([option.value])
            }}>
            <Plus className="w-3" />
          </div>
        )}
        {hasChildren && isParent && isOpen && (
          <div
            className="icon-wrapper"
            onClick={(e) => {
              e.stopPropagation()
              setOpen([])
            }}>
            <Minus className="w-3" />
          </div>
        )}
      </div>
    </li>
  )
}

const getSelectedParents = (selected, options) => {
  return selected.map((item) => {
    const option = options.find((option) => option.value === item)
    return option?.parent
  })
}
